/**
 * Native eSIM install via the LPA URL scheme.
 *
 * iOS 17.4+ and Android 13+ can install an eSIM straight from an LPA URL
 * (`LPA:1$smdp$activationCode`). On earlier iOS we fall back to Apple's
 * hosted esimsetup.apple.com URL, which triggers the same OS installer.
 * On older Android we fall back to showing the QR code (existing behaviour).
 *
 * The dashboard "Install eSIM" button calls `installEsim(order)`; the
 * function transparently handles native vs web + platform version.
 */

import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { isNative, isIOS, isAndroid } from './platform';

function buildLpaUrl(order) {
  // Prefer the raw LPA code stored on the order. Fall back to composing
  // it from SM-DP+ address + matching id if we only have the split fields.
  const lpa = order?.lpa_code || order?.activation_code;
  if (lpa && lpa.startsWith('LPA:')) return lpa;
  if (lpa && lpa.startsWith('1$')) return `LPA:${lpa}`;
  const smdp = order?.smdp_address;
  const matching = order?.matching_id;
  if (smdp && matching) return `LPA:1$${smdp}$${matching}`;
  return null;
}

function buildIosQuickInstallUrl(order) {
  // Universal link that opens the built-in eSIM installer on iOS 17.4+.
  const lpa = buildLpaUrl(order);
  if (!lpa) return null;
  // The `carddata` param is URL-encoded `1$smdp$matching`.
  const payload = lpa.replace(/^LPA:/, '');
  return `https://esimsetup.apple.com/esim_qrcode_provisioning?carddata=${encodeURIComponent(payload)}`;
}

/**
 * @returns {Promise<{ok: boolean, method: string, message?: string}>}
 */
export async function installEsim(order) {
  const lpa = buildLpaUrl(order);
  if (!lpa) {
    return { ok: false, method: 'none', message: 'This eSIM has no activation data yet.' };
  }

  // WEB (browser tab): keep the existing QR flow — nothing to invoke here.
  if (!isNative()) {
    return { ok: false, method: 'web-fallback', message: 'Use the QR code on this page or scan it from another device.' };
  }

  // iOS: try the direct LPA scheme first (17.4+). If it fails/silently
  // does nothing (older iOS), fall back to the Apple hosted URL which
  // opens the OS installer.
  if (isIOS()) {
    try {
      await App.openUrl({ url: lpa });
      return { ok: true, method: 'ios-lpa' };
    } catch (_) {
      const quickUrl = buildIosQuickInstallUrl(order);
      if (quickUrl) {
        await Browser.open({ url: quickUrl });
        return { ok: true, method: 'ios-quickinstall' };
      }
      return { ok: false, method: 'ios-fallback', message: 'Please install manually via Settings → Cellular → Add eSIM.' };
    }
  }

  // Android: the LPA scheme is supported system-wide on 13+ (some OEMs on 12+).
  if (isAndroid()) {
    try {
      await App.openUrl({ url: lpa });
      return { ok: true, method: 'android-lpa' };
    } catch (_) {
      return { ok: false, method: 'android-fallback', message: 'Open Settings → Network → SIMs → Add eSIM and scan the QR code below.' };
    }
  }

  return { ok: false, method: 'unknown' };
}
